import "./ProjectsModal.scss";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { colorOptions, normalizeColor } from "../../../../utils/colors";
import ColorPicker from "../../../ColorPicker/ColorPicker";
import EmptyStatePlaceholder from "../../../EmptyStatePlaceholder/EmptyStatePlaceholder";
import useAuthStore from "../../../../store/authStore";
import {
  createProject,
  updateProject,
  deleteProject,
  fetchProjectById,
  updateProjectMembers,
} from "../../../../api/projects";
import { getOrganizationMembers } from "../../../../api/organizations/organizations";
import { getTeams } from "../../../../api/teams/teams";

function ProjectsModal({ onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    user,
    projects,
    fetchProjects,
    setCurrentProject,
    currentProject,
    token,
    organizations,
  } = useAuthStore();
  const [searchText, setSearchText] = useState("");
  const [activePanel, setActivePanel] = useState(null); // 'add', 'edit', 'delete', 'manageGroup'
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [projectColor, setProjectColor] = useState(colorOptions[0]);
  const [showNameError, setShowNameError] = useState(false);
  const [contentReady, setContentReady] = useState(false); // Delay content rendering for smooth animation
  const [selectedPeople, setSelectedPeople] = useState([]); // Selected people for the project
  const [orgTeams, setOrgTeams] = useState([]); // All teams in the organization
  const [orgPeople, setOrgPeople] = useState([]); // All people in the organization
  const searchInputRef = useRef(null);
  const projectNameInputRef = useRef(null);
  const previousPanelRef = useRef(null); // Track previous panel state

  // Get current organization and check if user is admin
  const currentOrganization = organizations.find(
    (org) => org._id === user?.currentOrganization,
  );
  const userId = user?._id || user?.id;
  const isOrgAdmin =
    currentOrganization?.members.find(
      (m) => String(m.userId) === String(userId),
    )?.role === "admin";

  // Debug logging for isOrgAdmin
  // useEffect(() => {
  //     currentOrganization,
  //     userId,
  //     members: currentOrganization?.members,
  //     isOrgAdmin,
  //   });
  // }, [currentOrganization, userId, isOrgAdmin]);

  // Fetch projects when modal opens
  useEffect(() => {
    if (user) {
      fetchProjects();
    }
  }, [user, fetchProjects]);

  // Fetch organization teams and people when modal opens
  useEffect(() => {
    const fetchOrgData = async () => {
      if (!user?.currentOrganization || !token) {
        return;
      }

      // Check if the currentOrganization actually exists in the organizations array
      const currentOrg = organizations.find(
        (org) => org._id === user.currentOrganization,
      );

      if (!currentOrg) {
        setOrgTeams([]);
        setOrgPeople([]);
        return;
      }

      try {
        const [teams, people] = await Promise.all([
          getTeams(user.currentOrganization, token),
          getOrganizationMembers(user.currentOrganization, token),
        ]);
        setOrgTeams(teams || []);
        setOrgPeople(people || []);
      } catch (error) {
        console.error("Error fetching teams and people:", error);
        setOrgTeams([]);
        setOrgPeople([]);
      }
    };

    fetchOrgData();
  }, [user, token, organizations]);

  // Auto-focus search input when modal opens
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only focus search if there are projects
      if (projects && projects.length > 0) {
        searchInputRef.current?.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [projects]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
  };

  // Filter projects based on search text
  const filteredProjects = (projects || []).filter((project) =>
    project.name?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const handleEdit = (projectId) => {
    const project = (projects || []).find((p) => p.id === projectId);
    setSelectedProject(project);
    setProjectName(project.name);
    setProjectColor(normalizeColor(project.color));
    setShowNameError(false);
    setActivePanel("edit");
  };

  const handleDelete = (projectId) => {
    const project = (projects || []).find((p) => p.id === projectId);
    setSelectedProject(project);
    setActivePanel("delete");
  };

  const handleManageGroup = (projectId) => {
    const project = (projects || []).find((p) => p.id === projectId);
    setSelectedProject(project);

    // Load existing members
    const existingMemberIds = (project.members || []).map(
      (member) => member.userId?._id || member.userId,
    );

    // Filter out any member IDs that don't exist in orgPeople or are pending
    const validOrgPeopleIds = orgPeople
      .filter((p) => p.status !== "pending")
      .map((p) => p.id);

    const validMemberIds = existingMemberIds.filter((id) =>
      validOrgPeopleIds.includes(id),
    );

    setSelectedPeople(validMemberIds);
    setActivePanel("manageGroup");
  };
  const handleAddProject = () => {
    setSelectedProject(null);
    setProjectName("");
    setProjectColor(colorOptions[0]);
    setShowNameError(false);
    setActivePanel("add");
  };

  const handleClosePanel = () => {
    setActivePanel(null);
    setSelectedProject(null);
    setProjectName("");
    setProjectColor(colorOptions[0]);
    setShowNameError(false);
  };

  const handleSaveProject = async () => {
    if (projectName.trim()) {
      const colorHex = normalizeColor(projectColor);
      try {
        if (activePanel === "edit") {
          await updateProject(selectedProject.id, {
            projectName: projectName.trim(),
            color: colorHex,
          });

          // If editing the current project, update it in the store
          if (
            currentProject?.id === selectedProject.id ||
            currentProject?._id === selectedProject.id
          ) {
            const updatedProject = await fetchProjectById(
              selectedProject.id,
              user?.id || user?._id,
            );
            setCurrentProject(updatedProject);
          }

          // Refresh projects list
          await fetchProjects();
          handleClosePanel();

          // Don't close the modal after editing, just slide back
        } else {
          const newProject = await createProject({
            projectName: projectName.trim(),
            color: colorHex,
            ownerId: user._id || user.id,
            organizationId: user.currentOrganization,
          });

          // Refresh projects list
          await fetchProjects();

          // Select the newly created project
          if (newProject && (newProject.id || newProject._id)) {
            const projectId = newProject.id || newProject._id;
            const fullProject = await fetchProjectById(
              projectId,
              user?.id || user?._id,
            );
            setCurrentProject(fullProject);
          }

          // Close the modal
          onClose();

          // Redirect to tasks page
          navigate("/boards");
        }
      } catch (error) {
        console.error("Error saving project:", error);
        // TODO: Show error message to user
      }
    } else {
      setShowNameError(true);
    }
  };

  const handleDeleteProject = async () => {
    try {
      const isDeletingCurrentProject =
        currentProject?.id === selectedProject.id ||
        currentProject?._id === selectedProject.id;

      await deleteProject(selectedProject.id);

      // Refresh projects list
      await fetchProjects();
      handleClosePanel();

      // If we deleted the current project, clear it and redirect to help
      if (isDeletingCurrentProject) {
        setCurrentProject(null);
        onClose(); // Close the modal
        navigate("/help");
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      // TODO: Show error message to user
    }
  };

  const handleSelectProject = async (projectId) => {
    try {
      // Fetch the full project details from the backend
      const project = await fetchProjectById(projectId, user?.id || user?._id);

      // Set as current project in the store
      setCurrentProject(project);

      // Close the modal
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Error selecting project:", error);
      // TODO: Show error message to user
    }
  };

  // Auto-focus project name input when add/edit panel opens and content is ready
  useEffect(() => {
    if (
      (activePanel === "add" || activePanel === "edit") &&
      contentReady &&
      projectNameInputRef.current
    ) {
      // Small delay to ensure the input is fully rendered and focusable
      const timer = setTimeout(() => {
        projectNameInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [activePanel, contentReady]);

  // Handle delayed content rendering for smooth animation
  useEffect(() => {
    const wasExpanded = previousPanelRef.current !== null;
    const isExpanding = !wasExpanded && activePanel !== null;

    if (isExpanding) {
      // Panel is opening for the first time - delay content
      setContentReady(false);
      const timer = setTimeout(() => {
        setContentReady(true);
      }, 300); // Wait for slide animation
      previousPanelRef.current = activePanel;
      return () => clearTimeout(timer);
    } else if (activePanel) {
      // Panel is already open, just switching content - show immediately
      setContentReady(true);
      previousPanelRef.current = activePanel;
    } else {
      // Panel is closing
      setContentReady(false);
      previousPanelRef.current = null;
    }
  }, [activePanel]);

  const getPanelTitle = () => {
    switch (activePanel) {
      case "add":
        return t("projects.addNew");
      case "edit":
        return (
          <Trans
            i18nKey="projects.editTitle"
            values={{ name: selectedProject?.name || t("projects.project") }}
            components={{
              name: (
                <span
                  style={{
                    color: selectedProject
                      ? normalizeColor(selectedProject.color)
                      : "inherit",
                  }}
                />
              ),
            }}
          />
        );
      case "delete":
        return t("projects.removeTitle");
      case "manageGroup":
        return (
          <Trans
            i18nKey="projects.manageTeamTitle"
            values={{ name: selectedProject?.name || t("projects.project") }}
            components={{
              name: (
                <span style={{ color: selectedProject?.color || "#ffffff" }} />
              ),
            }}
          />
        );
      default:
        return "";
    }
  };

  return (
    <div className={`projects-modal-content ${activePanel ? "expanded" : ""}`}>
      <div className="title">
        <span>
          <Trans
            i18nKey="projects.titleIn"
            values={{
              name:
                currentOrganization?.name || t("organizations.organization"),
            }}
            components={{
              org: (
                <span
                  style={{ color: normalizeColor(currentOrganization?.color) }}
                />
              ),
            }}
          />
        </span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="main-wrapper">
        <div className="left">
          {/* Show search only if there are projects */}
          {projects && projects.length > 0 && (
            <div className="search-container">
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={t("projects.searchPlaceholder")}
                value={searchText}
                onChange={handleSearchChange}
              />
              <span
                className="material-symbols-outlined search-icon"
                style={searchText ? { cursor: "pointer" } : {}}
                onClick={searchText ? handleClearSearch : undefined}
              >
                {searchText ? "close" : "search"}
              </span>
            </div>
          )}

          <div className="project-list">
            {/* Show big centered button if no projects exist */}
            {projects && projects.length === 0 && isOrgAdmin ? (
              <div className="empty-state-container">
                <button
                  className="add-first-project-button"
                  onClick={handleAddProject}
                >
                  <span className="material-symbols-outlined">add</span>
                  <span>{t("projects.addFirst")}</span>
                </button>
              </div>
            ) : filteredProjects.length === 0 && searchText === "" ? (
              <EmptyStatePlaceholder
                topText={t("projects.emptyTitle")}
                bottomText={t("projects.emptyText")}
              />
            ) : (
              filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className={`project-item ${
                    selectedProject?.id === project.id ||
                    selectedProject?._id === project.id
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleSelectProject(project.id)}
                  style={{
                    cursor: "pointer",
                    border:
                      currentProject?.id === project.id ||
                      currentProject?._id === project.id
                        ? "1px solid var(--blue-button-color)"
                        : "1px solid transparent",
                  }}
                >
                  <div
                    className="project-color"
                    style={{ backgroundColor: project.color }}
                  ></div>
                  <div className="project-info">
                    <div className="project-name">{project.name}</div>
                    <div className="project-members">
                      {t("organizations.memberCount", {
                        count: project.members?.length || 0,
                      })}
                    </div>
                  </div>
                  {isOrgAdmin && (
                    <div className="project-actions">
                      <button
                        className="action-button group-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleManageGroup(project.id);
                        }}
                        title={t("projects.manageTeamTooltip")}
                      >
                        <span className="material-symbols-outlined">group</span>
                      </button>
                      <button
                        className="action-button edit-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(project.id);
                        }}
                        title={t("projects.editTooltip")}
                      >
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                      <button
                        className="action-button delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(project.id);
                        }}
                        title={t("projects.removeTooltip")}
                      >
                        <span className="material-symbols-outlined">
                          delete
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Show add button only if there are existing projects */}
          {isOrgAdmin && projects && projects.length > 0 && (
            <button className="add-project-button" onClick={handleAddProject}>
              <span className="material-symbols-outlined">add</span>
              {t("projects.addNew")}
            </button>
          )}
        </div>

        <div className={`right ${activePanel ? "active" : ""}`}>
          <div className="panel-header">
            <h3 className="panel-title">{getPanelTitle()}</h3>
            <button className="close-panel-button" onClick={handleClosePanel}>
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
          </div>

          <div className="panel-content">
            {contentReady && activePanel === "add" && (
              <div className="form-content">
                <div className="form-section">
                  <div className="form-label">{t("common.name")}</div>
                  <input
                    ref={projectNameInputRef}
                    type="text"
                    className="form-input"
                    placeholder={t("projects.namePlaceholder")}
                    value={projectName}
                    onChange={(e) => {
                      setProjectName(e.target.value);
                      if (showNameError && e.target.value.trim()) {
                        setShowNameError(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveProject();
                      }
                    }}
                  />
                  {showNameError && (
                    <div className="form-error">
                      {t("projects.nameRequired")}
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <div className="form-label">{t("common.color")}</div>
                  <div className="color-picker-wrapper">
                    <ColorPicker
                      selectedColor={projectColor}
                      onColorSelect={setProjectColor}
                    />
                  </div>
                </div>
              </div>
            )}

            {contentReady && activePanel === "edit" && selectedProject && (
              <div className="form-content">
                <div className="form-section">
                  <div className="form-label">{t("common.name")}</div>
                  <input
                    ref={projectNameInputRef}
                    type="text"
                    className="form-input"
                    placeholder={t("projects.namePlaceholder")}
                    value={projectName}
                    onChange={(e) => {
                      setProjectName(e.target.value);
                      if (showNameError && e.target.value.trim()) {
                        setShowNameError(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveProject();
                      }
                    }}
                  />
                  {showNameError && (
                    <div className="form-error">
                      {t("projects.nameRequired")}
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <div className="form-label">{t("common.color")}</div>
                  <div className="color-picker-wrapper">
                    <ColorPicker
                      selectedColor={projectColor}
                      onColorSelect={setProjectColor}
                    />
                  </div>
                </div>
              </div>
            )}

            {contentReady && activePanel === "delete" && selectedProject && (
              <div className="form-content">
                <p>
                  {t("projects.removeConfirm", { name: selectedProject.name })}
                </p>
              </div>
            )}

            {contentReady &&
              activePanel === "manageGroup" &&
              selectedProject && (
                <div className="form-content manage-group-content">
                  <div className="group-section">
                    <h4 className="section-title">{t("people.teams")}</h4>
                    <div className="group-list">
                      {orgTeams.length > 0 ? (
                        orgTeams.map((team) => (
                          <div
                            key={team.id}
                            className="group-item"
                            onClick={async () => {
                              // Add all team members to selected people
                              const teamMemberIds = (team.members || []).map(
                                (member) =>
                                  typeof member === "string"
                                    ? member
                                    : member.userId || member._id || member.id,
                              );

                              // Filter to only include members that exist in non-pending org people
                              const validOrgPeopleIds = orgPeople
                                .filter((p) => p.status !== "pending")
                                .map((p) => p.id);

                              const validTeamMemberIds = teamMemberIds.filter(
                                (id) => validOrgPeopleIds.includes(id),
                              );

                              const newSelectedPeople = [
                                ...new Set([
                                  ...selectedPeople,
                                  ...validTeamMemberIds,
                                ]),
                              ];
                              setSelectedPeople(newSelectedPeople);

                              // Auto-save
                              try {
                                await updateProjectMembers(selectedProject.id, {
                                  teamIds: [],
                                  memberIds: newSelectedPeople,
                                });
                                await fetchProjects();

                                // If updating the current project, refresh it in the store
                                if (
                                  currentProject?.id === selectedProject.id ||
                                  currentProject?._id === selectedProject.id
                                ) {
                                  const updatedProject = await fetchProjectById(
                                    selectedProject.id,
                                    user?.id || user?._id,
                                  );
                                  setCurrentProject(updatedProject);
                                }

                                // Dispatch event to refresh tasks if on Tasks page
                                window.dispatchEvent(
                                  new CustomEvent("projectMembersUpdated"),
                                );
                              } catch (error) {
                                console.error(
                                  "Error updating project members:",
                                  error,
                                );
                              }
                            }}
                          >
                            <div
                              className="team-color-dot"
                              style={{ backgroundColor: team.color }}
                            ></div>
                            <span className="group-item-name">{team.name}</span>
                          </div>
                        ))
                      ) : (
                        <p className="empty-message">
                          {t("projects.noTeamsAvailable")}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="group-section">
                    <h4 className="section-title">{t("people.people")}</h4>
                    <div className="group-list">
                      {orgPeople.length > 0 ? (
                        (() => {
                          // Sort people: non-pending first, then pending
                          const sortedPeople = [...orgPeople].sort((a, b) => {
                            const aIsPending = a.status === "pending";
                            const bIsPending = b.status === "pending";
                            if (aIsPending === bIsPending) return 0;
                            return aIsPending ? 1 : -1;
                          });

                          return sortedPeople.map((person) => (
                            <div
                              key={person.id}
                              className={`group-item ${
                                selectedPeople.includes(person.id)
                                  ? "selected"
                                  : ""
                              } ${person.id === (user?.id || user?._id) ? "current-user" : ""} ${person.status === "pending" ? "pending" : ""}`}
                              onClick={async () => {
                                // Prevent interaction with pending members
                                if (person.status === "pending") {
                                  return;
                                }

                                // Prevent removing yourself from the project
                                const currentUserId = user?.id || user?._id;
                                if (person.id === currentUserId) {
                                  return;
                                }

                                let newSelectedPeople;
                                if (selectedPeople.includes(person.id)) {
                                  newSelectedPeople = selectedPeople.filter(
                                    (id) => id !== person.id,
                                  );
                                } else {
                                  newSelectedPeople = [
                                    ...selectedPeople,
                                    person.id,
                                  ];
                                }

                                setSelectedPeople(newSelectedPeople);

                                // Auto-save
                                try {
                                  await updateProjectMembers(
                                    selectedProject.id,
                                    {
                                      teamIds: [],
                                      memberIds: newSelectedPeople,
                                    },
                                  );
                                  await fetchProjects();

                                  // If updating the current project, refresh it in the store
                                  if (
                                    currentProject?.id === selectedProject.id ||
                                    currentProject?._id === selectedProject.id
                                  ) {
                                    //   "Updating current project in store..."
                                    // );
                                    const updatedProject =
                                      await fetchProjectById(
                                        selectedProject.id,
                                        user?.id || user?._id,
                                      );
                                    //   "Updated current project:",
                                    //   updatedProject
                                    // );
                                    setCurrentProject(updatedProject);
                                  }

                                  // Dispatch event to refresh tasks if on Tasks page
                                  //   "Dispatching projectMembersUpdated event"
                                  // );
                                  window.dispatchEvent(
                                    new CustomEvent("projectMembersUpdated"),
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error updating project members:",
                                    error,
                                  );
                                }
                              }}
                            >
                              <div
                                className="user-avatar"
                                style={{
                                  backgroundColor:
                                    person.status === "pending"
                                      ? "#999"
                                      : person.role === "admin"
                                        ? "#4caf50"
                                        : "#2196f3",
                                }}
                              >
                                {(() => {
                                  const names = person.name?.split(" ") || [];
                                  if (names.length >= 2) {
                                    return (
                                      names[0].charAt(0).toUpperCase() +
                                      names[1].charAt(0).toUpperCase()
                                    );
                                  } else if (names.length === 1) {
                                    return names[0].charAt(0).toUpperCase();
                                  }
                                  return "?";
                                })()}
                              </div>
                              <span className="group-item-name">
                                {person.name}
                                {person.id === (user?.id || user?._id) &&
                                  t("people.youSuffix")}
                              </span>
                              {person.status === "pending" && (
                                <span className="role-badge pending-badge">
                                  {t("people.roles.pending")}
                                </span>
                              )}
                              {person.status !== "pending" &&
                                person.role === "admin" && (
                                  <span className="role-badge">
                                    {t("people.roles.admin")}
                                  </span>
                                )}
                            </div>
                          ));
                        })()
                      ) : (
                        <p className="empty-message">
                          {t("projects.noPeopleAvailable")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
          </div>

          <div className="panel-footer">
            {contentReady && activePanel === "add" && (
              <div className="form-buttons">
                <button className="cancel-button" onClick={handleClosePanel}>
                  {t("common.cancel")}
                </button>
                <button className="save-button" onClick={handleSaveProject}>
                  {t("common.create")}
                </button>
              </div>
            )}

            {contentReady && activePanel === "edit" && (
              <div className="form-buttons">
                <button className="cancel-button" onClick={handleClosePanel}>
                  {t("common.cancel")}
                </button>
                <button className="save-button" onClick={handleSaveProject}>
                  {t("common.save")}
                </button>
              </div>
            )}

            {contentReady && activePanel === "delete" && (
              <div className="delete-actions">
                <button
                  className="confirm-delete-button"
                  onClick={handleDeleteProject}
                >
                  {t("common.remove")}
                </button>
                <button className="cancel-button" onClick={handleClosePanel}>
                  {t("common.cancel")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectsModal;
